import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { EightDReport, D3RootCause, D4PermanentCorrectiveAction } from '../types/eightd.types';

// D3: Root Cause Analysis
export const D3RootCauseComponent: React.FC<{
    report: Partial<EightDReport>;
    updateField: (path: string, value: string | number | boolean | object | null | undefined) => void;
    setReport: React.Dispatch<React.SetStateAction<Partial<EightDReport>>>;
}> = ({ report, setReport }) => {
    const addRootCause = () => {
        const newCause: D3RootCause = {
            id: `rc-${Date.now()}`,
            description: '',
            category: 'Process',
            method: '5-Why',
            analysis: '',
            verified: false,
        };
        setReport(prev => ({
            ...prev,
            d3_rootCause: [...(prev.d3_rootCause || []), newCause],
        }));
    };

    const updateRootCause = (id: string, field: string, value: string | number | boolean | Date) => {
        setReport(prev => ({
            ...prev,
            d3_rootCause: prev.d3_rootCause?.map(cause =>
                cause.id === id ? { ...cause, [field]: value } : cause
            ),
        }));
    };

    const removeRootCause = (id: string) => {
        setReport(prev => ({
            ...prev,
            d3_rootCause: prev.d3_rootCause?.filter(cause => cause.id !== id),
        }));
    };

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">D3: Identify and Verify Root Causes</h3>
                <p className="text-sm text-gray-600 mb-6">
                    Use RCA methods to identify and verify the true root causes
                </p>
            </div>

            <div className="flex justify-between items-center">
                <h4 className="font-medium text-gray-900">Root Causes</h4>
                <button
                    onClick={addRootCause}
                    className="flex items-center gap-2 px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    Add Root Cause
                </button>
            </div>

            {report.d3_rootCause && report.d3_rootCause.length > 0 ? (
                <div className="space-y-4">
                    {report.d3_rootCause.map((cause, index) => (
                        <div key={cause.id} className="p-4 border border-gray-200 rounded-lg">
                            <div className="flex items-start justify-between mb-4">
                                <h5 className="font-medium text-gray-900">Root Cause #{index + 1}</h5>
                                <button
                                    onClick={() => removeRootCause(cause.id)}
                                    className="text-red-600 hover:text-red-700"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>

                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Root Cause Description *
                                    </label>
                                    <textarea
                                        value={cause.description}
                                        onChange={(e) => updateRootCause(cause.id, 'description', e.target.value)}
                                        rows={2}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Describe the root cause..."
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Category
                                    </label>
                                    <select
                                        value={cause.category}
                                        onChange={(e) => updateRootCause(cause.id, 'category', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="Design">Design</option>
                                        <option value="Process">Process</option>
                                        <option value="Material">Material</option>
                                        <option value="Human Error">Human Error</option>
                                        <option value="Environment">Environment</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Analysis Method
                                    </label>
                                    <select
                                        value={cause.method}
                                        onChange={(e) => updateRootCause(cause.id, 'method', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="5-Why">5-Why</option>
                                        <option value="Fishbone">Fishbone</option>
                                        <option value="FTA">FTA</option>
                                        <option value="Pareto">Pareto</option>
                                    </select>
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Analysis Details
                                    </label>
                                    <textarea
                                        value={cause.analysis}
                                        onChange={(e) => updateRootCause(cause.id, 'analysis', e.target.value)}
                                        rows={3}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Explain the analysis that led to this root cause..."
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={cause.verified}
                                            onChange={(e) => updateRootCause(cause.id, 'verified', e.target.checked)}
                                            className="rounded"
                                        />
                                        <span className="text-sm font-medium text-gray-700">
                                            Root cause verified with data/evidence
                                        </span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="p-8 bg-gray-50 border border-gray-200 rounded-lg text-center">
                    <p className="text-gray-600">No root causes identified yet</p>
                    <button
                        onClick={addRootCause}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Add First Root Cause
                    </button>
                </div>
            )}
        </div>
    );
};

// D4: Permanent Corrective Actions
export const D4CorrectiveActions: React.FC<{
    report: Partial<EightDReport>;
    updateField: (path: string, value: string | number | boolean | object | null | undefined) => void;
    setReport: React.Dispatch<React.SetStateAction<Partial<EightDReport>>>;
}> = ({ report, setReport }) => {
    const addAction = () => {
        const newAction: D4PermanentCorrectiveAction = {
            id: `pca-${Date.now()}`,
            description: '',
            type: 'Process Change',
            responsibility: '',
            responsibilityName: '',
            dueDate: new Date(),
            status: 'Planned',
            validationPlan: '',
        };
        setReport(prev => ({
            ...prev,
            d4_correctiveAction: [...(prev.d4_correctiveAction || []), newAction],
        }));
    };

    const updateAction = (id: string, field: string, value: string | number | boolean | Date) => {
        setReport(prev => ({
            ...prev,
            d4_correctiveAction: prev.d4_correctiveAction?.map(action =>
                action.id === id ? { ...action, [field]: value } : action
            ),
        }));
    };

    const removeAction = (id: string) => {
        setReport(prev => ({
            ...prev,
            d4_correctiveAction: prev.d4_correctiveAction?.filter(action => action.id !== id),
        }));
    };

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">D4: Implement Permanent Corrective Actions</h3>
                <p className="text-sm text-gray-600 mb-6">
                    Define and implement permanent solutions to eliminate root causes
                </p>
            </div>

            <div className="flex justify-between items-center">
                <h4 className="font-medium text-gray-900">Corrective Actions</h4>
                <button
                    onClick={addAction}
                    className="flex items-center gap-2 px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    Add Action
                </button>
            </div>

            {report.d4_correctiveAction && report.d4_correctiveAction.length > 0 ? (
                <div className="space-y-4">
                    {report.d4_correctiveAction.map((action, index) => (
                        <div key={action.id} className="p-4 border border-gray-200 rounded-lg">
                            <div className="flex items-start justify-between mb-4">
                                <h5 className="font-medium text-gray-900">Action #{index + 1}</h5>
                                <button
                                    onClick={() => removeAction(action.id)}
                                    className="text-red-600 hover:text-red-700"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>

                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Corrective Action Description *
                                    </label>
                                    <textarea
                                        value={action.description}
                                        onChange={(e) => updateAction(action.id, 'description', e.target.value)}
                                        rows={2}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Describe the permanent corrective action..."
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Action Type
                                    </label>
                                    <select
                                        value={action.type}
                                        onChange={(e) => updateAction(action.id, 'type', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="Design Change">Design Change</option>
                                        <option value="Process Change">Process Change</option>
                                        <option value="Training">Training</option>
                                        <option value="Tooling">Tooling</option>
                                        <option value="Specification Change">Specification Change</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Responsible Person
                                    </label>
                                    <input
                                        type="text"
                                        value={action.responsibilityName}
                                        onChange={(e) => updateAction(action.id, 'responsibilityName', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Due Date
                                    </label>
                                    <input
                                        type="date"
                                        value={action.dueDate instanceof Date ? action.dueDate.toISOString().split('T')[0] : ''}
                                        onChange={(e) => updateAction(action.id, 'dueDate', new Date(e.target.value))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Status
                                    </label>
                                    <select
                                        value={action.status}
                                        onChange={(e) => updateAction(action.id, 'status', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="Planned">Planned</option>
                                        <option value="In Progress">In Progress</option>
                                        <option value="Completed">Completed</option>
                                        <option value="Verified">Verified</option>
                                    </select>
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Validation Plan
                                    </label>
                                    <textarea
                                        value={action.validationPlan}
                                        onChange={(e) => updateAction(action.id, 'validationPlan', e.target.value)}
                                        rows={2}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="How will this action be validated?"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="p-8 bg-gray-50 border border-gray-200 rounded-lg text-center">
                    <p className="text-gray-600">No corrective actions added yet</p>
                    <button
                        onClick={addAction}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Add First Action
                    </button>
                </div>
            )}
        </div>
    );
};

// D5: Validation
export const D5Validation: React.FC<{
    report: Partial<EightDReport>;
    updateField: (path: string, value: string | number | boolean | object | null | undefined) => void
}> = ({ report, updateField }) => {
    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">D5: Validate Corrective Actions</h3>
                <p className="text-sm text-gray-600 mb-6">
                    Verify that the corrective actions eliminate the root causes and do not cause undesirable effects
                </p>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Validation Method *
                </label>
                <textarea
                    value={report.d5_validation?.validationMethod || ''}
                    onChange={(e) => updateField('d5_validation.validationMethod', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Describe the validation method (e.g., production trial, testing, analysis)..."
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Validation Criteria *
                </label>
                <textarea
                    value={report.d5_validation?.validationCriteria || ''}
                    onChange={(e) => updateField('d5_validation.validationCriteria', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="What are the success criteria? What measurements will be used?"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Validation Results *
                </label>
                <textarea
                    value={report.d5_validation?.validationResults || ''}
                    onChange={(e) => updateField('d5_validation.validationResults', e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Document the validation results and findings..."
                />
            </div>

            <div className="pt-4 border-t border-gray-200">
                <label className="flex items-center gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={report.d5_validation?.approved || false}
                        onChange={(e) => updateField('d5_validation.approved', e.target.checked)}
                        className="w-5 h-5 rounded"
                    />
                    <span className="font-medium text-gray-900">
                        Validation approved - Actions are effective and eliminate root causes
                    </span>
                </label>
            </div>
        </div>
    );
};

// Continued in Eightd Components Part 3...
